/* @flow */

import {CharacterMetadata, ContentBlock, genKey, Entity} from 'draft-js';
import {Map, List, OrderedMap, OrderedSet} from 'immutable';
import getSafeBodyFromHTML from './getSafeBodyFromHTML';
import {
	createTextChunk,
	getSoftNewlineChunk,
	getEmptyChunk,
	getBlockDividerChunk,
	getFirstBlockChunk,
	getAtomicBlockChunk,
	joinChunks,
	getHorizontalRuleChunk,
} from './chunkBuilder';
import getBlockTypeForTag from './getBlockTypeForTag';
import processInlineTag from './processInlineTag';
import getBlockData from './getBlockData';
import getEntityId from './getEntityId';
import getJiraCustomBlockType from './getJiraCustomBlockType';
import {
	FILE_CHIP_CLASS,
	FILE_CHIP_FALLBACK_LABEL,
	FILE_REF_ATTRIBUTE,
	isFileReference as isForecastFileReference,
	parsedMediaMetadata,
} from '../../../task-modal/inlineImages/fileReference';

const SPACE = ' ';
const REGEX_NBSP = new RegExp('&nbsp;', 'g');

// Match a class by token rather than exact string: Plate appends its own classes (selection/hover)
// to a node on blur, so an exact `===` would miss `slate-non-editable-content slate-selected` and
// silently drop the Jira custom block (PIKA-542). classList is undefined on text nodes, so fall
// back to the exact check there (where it is simply false). Mirrors getEntityId.js's helper.
const hasClass = (node, token) => (node.classList ? node.classList.contains(token) : node.className === token);

let firstBlock = true;

function hasOnlyOneAttribute(node) {
	return node.attributes.length === 1;
}

function isEmptyNoneditableDiv(node) {
	return hasOnlyOneAttribute(node) && node.attributes.contenteditable?.value === 'false';
}

function genFragment(
	node,
	inlineStyle,
	depth,
	lastList,
	inEntity,
	customChunkGenerator,
	isInList = false,
	inNoneditable = false
) {
	const nodeName = node.nodeName.toLowerCase();
	// Makes sure nodes marked as 'draftjs-ignore' are not rendered as individual blocks
	const draftJsIgnore = node.className && node.className.includes ? node.className?.includes('slate-draftjs-ignore') : false;

	if (customChunkGenerator) {
		const value = customChunkGenerator(nodeName, node);
		if (value) {
			const entityId = Entity.__create(value.type, value.mutability, value.data || {});
			return {chunk: getAtomicBlockChunk(entityId)};
		}
	}

	if (nodeName === '#text' && node.textContent !== '\n') {
		return createTextChunk(node, inlineStyle, inEntity);
	}

	if (nodeName === 'br') {
		return {chunk: getSoftNewlineChunk()};
	}

	if (nodeName === 'hr') {
		return {chunk: getHorizontalRuleChunk()};
	}

	if (nodeName === 'img' && node instanceof HTMLImageElement) {
		const entityConfig = {};
		entityConfig.src = node.getAttribute ? node.getAttribute('src') || node.src : node.src;
		entityConfig.alt = node.alt;
		entityConfig.height = node.style.height;
		entityConfig.width = node.style.width;
		if (node.style.float) {
			entityConfig.alignment = node.style.float;
		}
		const entityId = Entity.__create('IMAGE', 'MUTABLE', entityConfig);
		return {chunk: getAtomicBlockChunk(entityId)};
	}

	// An inline video or audio player. Both are atomic blocks: they own their line, as
	// an image does.
	//
	// Only our own references are accepted. A media tag pasted from a web page carries a
	// third-party src, which would be persisted and refetched from someone else's host
	// on every render — permanently broken as soon as they rotate it. Entities of
	// exactly that shape sit in stored content already, so the emit side drops them too
	// rather than relying on this guard to catch them after a save.
	//
	// A rejected player falls through rather than returning, so the walk below still
	// collects its children. Those children are the fallback content a browser shows
	// when it cannot play the source — the author's own text and links, not part of the
	// embed — and returning here deleted them along with the player. For a bare <audio>
	// wrapping a caption that removed the whole line.
	if (nodeName === 'video' || nodeName === 'audio') {
		const src = node.getAttribute('src');
		if (isForecastFileReference(src)) {
			// `height`, `width` and `alignment` are deliberately NOT read, even though the
			// <img> branch above reads all three.
			//
			// Nothing emits them for a player and nothing renders them either: the player
			// element sizes itself in CSS and ignores all three. Reading them would make them
			// write-only — parsed in, never emitted out, so they vanish on the next save while
			// looking supported. If a player ever needs geometry, the emit side has to come
			// first.
			//
			// Size and mime type ARE read, through the same parse the chip uses. Both the emit
			// side here and the design system's serializer put them on a player, because a
			// player falls back to a chip whenever the browser cannot decode the file or no
			// adapter resolves it — and the chip can only show what the last load restored.
			// Dropping them here would strip them on the first save and leave the fallback
			// showing a bare filename for ever after.
			//
			// The filename comes from data-name. `HTMLVideoElement` has no `alt` property, so
			// reading one yields undefined for every player.
			const entityId = Entity.__create(nodeName.toUpperCase(), 'MUTABLE', {src, ...parsedMediaMetadata(node)});
			return {chunk: getAtomicBlockChunk(entityId)};
		}
	}

	// A file chip with an empty label still has to keep its reference.
	//
	// Entities are attached per character here, so a span with no text has no character
	// to carry one: the chip is dropped and the reference goes with it, silently, and
	// the next save stores content that no longer mentions the file at all. The design
	// system's serializer emits exactly this shape for a file with no name, and its chip
	// renders the icon alone in that state, so an empty label is a supported state on
	// one side and an unreadable one on the other.
	//
	// Anchoring the entity to the same fallback label the emit side writes closes that.
	// The name attribute is left as it was found, so a nameless chip still renders as
	// just the icon once it is back in the editor.
	//
	// `inEntity` is the FILE entity getEntityId just created for this node — the caller
	// passes it down for exactly this walk — and the reference is re-checked so an
	// ancestor's entity can never be anchored to a chip this parse rejected.
	if (
		nodeName === 'span' &&
		!node.textContent &&
		inEntity &&
		hasClass(node, FILE_CHIP_CLASS) &&
		isForecastFileReference(node.getAttribute(FILE_REF_ATTRIBUTE))
	) {
		const label = node.getAttribute('data-name') || FILE_CHIP_FALLBACK_LABEL;
		const style = processInlineTag(nodeName, node, inlineStyle);
		return {
			chunk: {
				text: label,
				inlines: Array(label.length).fill(style),
				entities: Array(label.length).fill(inEntity),
				blocks: [],
			},
		};
	}

	if (nodeName === 'iframe' && node instanceof HTMLIFrameElement) {
		const entityConfig = {};
		entityConfig.src = node.getAttribute ? node.getAttribute('src') || node.src : node.src;
		entityConfig.height = node.height;
		entityConfig.width = node.width;
		const entityId = Entity.__create('EMBEDDED_LINK', 'MUTABLE', entityConfig);
		return {chunk: getAtomicBlockChunk(entityId)};
	}

	// Look for the blocktype only if the node has not been marked as 'draftjs-ignore'
	let blockType = draftJsIgnore ? undefined : getBlockTypeForTag(nodeName, lastList);

	if (blockType === 'unstyled') {
		// Look for Jira custom block types. These are spans within a paragraph with the className 'slate-non-editable-content'
		// Check first chiled to see if it matches.
		const childNode = node.firstChild;
		if (childNode?.nodeName?.toLowerCase() === 'span' && hasClass(childNode, 'slate-non-editable-content')) {
			// Set blockType to undefined so this doesn't get rendered as a separate block. The Child span will get renderes as a custom jira block instead
			blockType = undefined;
		}

		if (isEmptyNoneditableDiv(node)) {
			// Set blockType to undefined so this doesn't get rendered as a separate block. This is a patch for some plate markup that we can safely ignore
			blockType = undefined;
		}
	}
	const isNoneditableSpan = nodeName === 'span' && hasClass(node, 'slate-non-editable-content');
	if (!blockType && !draftJsIgnore) {
		// If a blocktype wasn't found, check if it is a noneditable span and set the blockType from the data attribute
		if (isNoneditableSpan) {
			blockType = getJiraCustomBlockType(node);
		}
	}

	// A Jira noneditable block is opaque: once its span has produced a block, descendants must NOT
	// derive their own block types. Otherwise a nested block-producing element (e.g. a nested
	// noneditable span) emits a duplicate block alongside the custom block already created (PIKA-542
	// #8 / Task 7). The subtree's text is still collected below; only extra block creation is suppressed.
	if (inNoneditable) {
		blockType = undefined;
	}

	let chunk;
	if (blockType) {
		if (nodeName === 'ul' || nodeName === 'ol') {
			isInList = true;
			lastList = nodeName;
			depth += 1;
		} else {
			if (!isInList && blockType !== 'unordered-list-item' && blockType !== 'ordered-list-item') {
				lastList = '';
				depth = -1;
			}
			if (firstBlock) {
				chunk = getFirstBlockChunk(blockType, getBlockData(node));
				firstBlock = false;
			} else if (!isInList || blockType !== 'unstyled') {
				chunk = getBlockDividerChunk(blockType, depth, getBlockData(node));
			}
		}
	}
	if (!chunk) {
		chunk = getEmptyChunk();
	}

	inlineStyle = processInlineTag(nodeName, node, inlineStyle);

	let child = node.firstChild;
	const childInNoneditable = inNoneditable || isNoneditableSpan;
	while (child) {
		const entityId = getEntityId(child);
		const {chunk: generatedChunk} = genFragment(
			child,
			inlineStyle,
			depth,
			lastList,
			entityId || inEntity,
			customChunkGenerator,
			isInList,
			childInNoneditable
		);
		chunk = joinChunks(chunk, generatedChunk);
		const sibling = child.nextSibling;
		child = sibling;
	}
	return {chunk};
}

function getChunkForHTML(html, customChunkGenerator) {
	const sanitizedHtml = html.trim().replace(REGEX_NBSP, SPACE);
	const safeBody = getSafeBodyFromHTML(sanitizedHtml);
	if (!safeBody) {
		return null;
	}
	firstBlock = true;
	const {chunk} = genFragment(safeBody, new OrderedSet(), -1, '', undefined, customChunkGenerator);
	return {chunk};
}

export default function htmlToDraft(html, customChunkGenerator) {
	const chunkData = getChunkForHTML(html, customChunkGenerator);
	if (chunkData) {
		const {chunk} = chunkData;
		let entityMap = new OrderedMap({});
		chunk.entities &&
			chunk.entities.forEach(entity => {
				if (entity) {
					entityMap = entityMap.set(entity, Entity.__get(entity));
				}
			});
		let start = 0;
		return {
			contentBlocks: chunk.text.split('\r').map((textBlock, ii) => {
				const end = start + textBlock.length;
				const inlines = chunk && chunk.inlines.slice(start, end);
				const entities = chunk && chunk.entities.slice(start, end);
				const characterList = new List(
					inlines.map((style, index) => {
						const data = {style, entity: null};
						if (entities[index]) {
							data.entity = entities[index];
						}
						return CharacterMetadata.create(data);
					})
				);
				start = end;
				return new ContentBlock({
					key: genKey(),
					type: (chunk && chunk.blocks[ii] && chunk.blocks[ii].type) || 'unstyled',
					depth: chunk && chunk.blocks[ii] && chunk.blocks[ii].depth,
					data: (chunk && chunk.blocks[ii] && chunk.blocks[ii].data) || new Map({}),
					text: textBlock,
					characterList,
				});
			}),
			entityMap,
		};
	}
	return null;
}
